import { createContext, ReactNode, useContext, useState } from "react";
import { LinkValues } from "../types/link.type";

export interface Alert {
    id: string;
    type: string;
    text: string;
    link?: LinkValues;
}

export interface AlertContextValue {
    alerts: Alert[];
    success: (text: string | string[], link?: LinkValues) => void;
    info: (text: string | string[], link?: LinkValues) => void;
    warning: (text: string | string[], link?: LinkValues) => void;
    error: (text: string | string[], link?: LinkValues) => void;
    remove: (alert: Alert) => void;
    clear: () => void;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

type Props = {
    children: ReactNode;
}

export const AlertProvider = ({ children }: Props) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const add = (type: string, text: string | string[], link?: LinkValues) => {
        if (Array.isArray(text)) {
            const newAlerts = text.map(t => ({ 
                id: crypto.randomUUID(), 
                type, 
                text: t, 
                link 
            }));
            setAlerts(alerts => [...alerts, ...newAlerts]);
            return;
        }
        const alert: Alert = { 
                id: crypto.randomUUID(), 
                type, 
                text,
                link,
            };
        setAlerts(alerts => [...alerts, alert]);
    }

    const success = (text: string | string[], link?: LinkValues) => add('success', text, link);
    const info = (text: string | string[], link?: LinkValues) => add('primary', text, link);
    const warning = (text: string | string[], link?: LinkValues) => add('warning', text, link);
    const error = (text: string | string[], link?: LinkValues) => add('danger', text, link);
    const remove = (alert: Alert) => setAlerts(alerts => alerts.filter(a => a !== alert));
    const clear = () => setAlerts([]);

    const value: AlertContextValue = {
        alerts,
        success,
        info,
        warning,
        error,
        remove,
        clear,
    };

    return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (context === undefined) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};